import React, { useMemo, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Camera, ChevronLeft, Clock } from "lucide-react";
import {
  useGetAttendanceEntriesQuery,
} from "../../slices/attendanceApiSlice";
import { reverseGeocode } from "../../utils/geocode";
import { ATTENDANCE_URL, API_BASE_URL } from "../../constants";
import { useSelector } from "react-redux";
import LateReasonDialog from "../../components/LateReasonDialog/LateReasonDialog";

export default function AttendanceRecordDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Get auth token and user role from Redux
  // Get auth token and user role from Redux
  const token = useSelector((state) => state.auth?.token);
  const userRole = useSelector((state) => state.auth?.userRole);

  // Determine if user is admin/CFM/superadmin
  const isAdminRole = [
    "admin",
    "Admin",
    "super_admin",
    "Super Admin",
    "cfm",
    "CFM",
  ].includes(userRole);

  // State for storing blob URLs of images
  const [clockInImageUrl, setClockInImageUrl] = useState(null);
  const [clockOutImageUrl, setClockOutImageUrl] = useState(null);
  const [imageLoadingStates, setImageLoadingStates] = useState({
    clockIn: false,
    clockOut: false,
  });
  const [showLateReasonDialog, setShowLateReasonDialog] = useState(false);
  const [selectedLateReason, setSelectedLateReason] = useState("");
  const [clockInAddress, setClockInAddress] = useState(null);
  const [clockOutAddress, setClockOutAddress] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState({
    clockIn: false,
    clockOut: false,
  });

  // Get attendance entries
  const {
    data: entriesData,
    isLoading: isLoadingEntries,
    error: entriesError,
  } = useGetAttendanceEntriesQuery(id, {
    skip: !id || !userRole, // Wait until we have an id and the user's role is determined
  });

  const finalEntriesData = entriesData;
  const finalIsLoading = isLoadingEntries || !userRole;
  const finalError = entriesError;

  // Transform API response to match expected format
  const record = useMemo(() => {
    if (
      !finalEntriesData ||
      !finalEntriesData.entries ||
      finalEntriesData.entries.length === 0
    ) {
      return null;
    }

    // Get clock in and clock out entries
    const clockInEntry = finalEntriesData.entries.find(
      (e) => e.entryType === "CLOCK_IN",
    );
    const clockOutEntry = finalEntriesData.entries.find(
      (e) => e.entryType === "CLOCK_OUT",
    );

    if (!clockInEntry) {
      return null;
    }

    // Build image URLs using the correct API endpoint format
    const buildImageUrl = (imagePath) => {
      if (!imagePath) return null;
      // If it's already a full URL, return as is
      if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
        return imagePath;
      }

      // If the path contains "attendance_images/", optimize for serving
      if (imagePath.includes("attendance_images/")) {
        // Return a path that the frontend can use directly or via API fallback
        return imagePath;
      }

      return imagePath;
    };

    return {
      id: finalEntriesData.attendance_id,
      date: clockInEntry.time.split("T")[0],
      clockIn: clockInEntry.time
        ? new Date(clockInEntry.time).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
        : null,
      clockOut: clockOutEntry?.time
        ? new Date(clockOutEntry.time).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
        : null,
      location: clockInEntry.location?.city || "-",
      locationCoords: clockInEntry.location
        ? {
          lat: clockInEntry.location.latitude,
          lng: clockInEntry.location.longitude,
        }
        : null,
      clockOutLocation: clockOutEntry?.location?.city || "-",
      clockOutCoords: clockOutEntry?.location
        ? {
          lat: clockOutEntry.location.latitude,
          lng: clockOutEntry.location.longitude,
        }
        : null,
      photo: buildImageUrl(clockInEntry.imagePath),
      clockOutPhoto: clockOutEntry
        ? buildImageUrl(clockOutEntry.imagePath)
        : null,
      type: clockOutEntry ? "present" : "half",
      lateReason: clockInEntry.lateReason || null,
    };

    return record;
  }, [entriesData]);

  // Check if record is older than 90 days
  const recordDate = record ? new Date(record.date + "T00:00:00") : null;
  const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
  const cleared = recordDate
    ? Date.now() - recordDate.getTime() > ninetyDaysMs
    : false;

  // Effect to fetch images when record changes
  useEffect(() => {
    let clockInUrl = null;
    let clockOutUrl = null;
    let isCancelled = false;

    // Function to fetch image with authorization header
    const fetchImageWithAuth = async (imagePath) => {
      if (!imagePath || !token) return null;

      // Decide which URL to use
      // Priority 1: Direct path (Nginx serves it)
      // Priority 2: API Gateway path (Fallback)
      const directUrl = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
      const apiUrl = `${API_BASE_URL}${ATTENDANCE_URL}/attendance/image?path=${imagePath}`;

      try {
        // Try direct URL first
        console.log("AttendanceDetail - Trying direct fetch:", directUrl);
        let response = await fetch(directUrl, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` }
        });

        // If direct fails, try API URL
        if (!response.ok) {
          console.warn("AttendanceDetail - Direct fetch failed, trying API fallback:", apiUrl);
          response = await fetch(apiUrl, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        if (!response.ok) {
          console.error("AttendanceDetail - Both fetch attempts failed:", response.status);
          return null;
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } catch (error) {
        console.error("AttendanceDetail - Fetch error:", error);
        return null;
      }
    };

    const loadImages = async () => {
      if (record && !cleared && !isCancelled) {
        // Load clock in image
        if (record.photo) {
          setImageLoadingStates((prev) => ({ ...prev, clockIn: true }));
          clockInUrl = await fetchImageWithAuth(record.photo);
          if (!isCancelled) {
            setClockInImageUrl(clockInUrl);
            setImageLoadingStates((prev) => ({ ...prev, clockIn: false }));
          }
        }

        // Load clock out image
        if (record.clockOutPhoto) {
          setImageLoadingStates((prev) => ({ ...prev, clockOut: true }));
          clockOutUrl = await fetchImageWithAuth(record.clockOutPhoto);
          if (!isCancelled) {
            setClockOutImageUrl(clockOutUrl);
            setImageLoadingStates((prev) => ({ ...prev, clockOut: false }));
          }
        }
      }
    };

    loadImages();

    // Reverse geocode locations
    const geocodeLocations = async () => {
      if (record?.locationCoords) {
        setLoadingAddresses(prev => ({ ...prev, clockIn: true }));
        try {
          const addr = await reverseGeocode(record.locationCoords.lat, record.locationCoords.lng);
          setClockInAddress(addr);
        } catch (err) {
          console.error("Failed to geocode clock-in location", err);
        } finally {
          setLoadingAddresses(prev => ({ ...prev, clockIn: false }));
        }
      }

      if (record?.clockOutCoords) {
        setLoadingAddresses(prev => ({ ...prev, clockOut: true }));
        try {
          const addr = await reverseGeocode(record.clockOutCoords.lat, record.clockOutCoords.lng);
          setClockOutAddress(addr);
        } catch (err) {
          console.error("Failed to geocode clock-out location", err);
        } finally {
          setLoadingAddresses(prev => ({ ...prev, clockOut: false }));
        }
      }
    };

    geocodeLocations();

    // Cleanup function to revoke blob URLs and cancel pending updates
    return () => {
      isCancelled = true;
      if (clockInUrl) URL.revokeObjectURL(clockInUrl);
      if (clockOutUrl) URL.revokeObjectURL(clockOutUrl);
    };
  }, [record, token, cleared, finalEntriesData]);

  const BackButton = () => (
    <button
      onClick={() => navigate(-1)}
      className="w-10 h-10 rounded-full flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
      aria-label="Go back"
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  );
  const buildOsmLinks = (coords) => {
    if (!coords) return null;

    // Handle both {lat, lng} and {lat, lon} formats
    const lat = coords.lat ?? coords.latitude;
    const lng = coords.lng ?? coords.longitude ?? coords.lon;

    if (
      typeof lat !== "number" ||
      typeof lng !== "number" ||
      isNaN(lat) ||
      isNaN(lng)
    )
      return null;

    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const clampedLat = clamp(lat, -90, 90);
    const clampedLng = clamp(lng, -180, 180);

    // Use a simpler embed format with zoom and marker
    const zoom = 15;
    const toFixed = (num) => Number(num).toFixed(6);
    const latStr = toFixed(clampedLat);
    const lngStr = toFixed(clampedLng);

    // Calculate bbox for embed (small area around the point)
    // Format: bbox=minLon,minLat,maxLon,maxLat (west, south, east, north)
    const delta = 0.01;
    const minLon = toFixed(clamp(clampedLng - delta, -180, 180));
    const minLat = toFixed(clamp(clampedLat - delta, -90, 90));
    const maxLon = toFixed(clamp(clampedLng + delta, -180, 180));
    const maxLat = toFixed(clamp(clampedLat + delta, -90, 90));
    const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;

    // OpenStreetMap embed format: bbox=west,south,east,north
    // Marker format: lat,lon (no encoding needed in URL)
    const marker = `${latStr},${lngStr}`;

    return {
      embedUrl: `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`,
      linkUrl: `https://www.openstreetmap.org/?mlat=${latStr}&mlon=${lngStr}#map=${zoom}/${latStr}/${lngStr}`,
      lat: latStr,
      lng: lngStr,
    };
  };

  const renderLocationCard = (title, icon, locationText, coords) => {
    const mapLinks = buildOsmLinks(coords);
    return (
      <div>
        <div className="flex items-center mb-3">
          {icon}
          <span className="text-zinc-800">{title}</span>
        </div>
        <div className="rounded-lg overflow-hidden border">
          {mapLinks ? (
            <>
              <iframe
                title={`${title} map`}
                src={mapLinks.embedUrl}
                className="w-full h-64 border-0"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                style={{ minHeight: "256px" }}
              />
              <a
                href={mapLinks.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-xs bg-zinc-50 px-3 py-2 border-t hover:bg-zinc-100 transition-colors"
              >
                <div className="flex flex-col gap-1">
                  <span className="font-medium text-zinc-900">
                    {title === "Clock in location" ? (loadingAddresses.clockIn ? "Loading address..." : clockInAddress || locationText) : (loadingAddresses.clockOut ? "Loading address..." : clockOutAddress || locationText)}
                  </span>
                  <span className="text-zinc-500 font-mono">
                    ({mapLinks.lat}, {mapLinks.lng})
                  </span>
                </div>
              </a>
            </>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center bg-zinc-100 px-4 text-center">
              <span className="text-zinc-600 mb-2">
                {locationText &&
                  locationText !== "Gyalpozhing, Mongar Bhutan" &&
                  locationText !== "-"
                  ? locationText
                  : "No location"}
              </span>
              {coords && (
                <span className="text-xs text-zinc-400">
                  Coordinates not available for map display
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (finalIsLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4 mb-4">
          <BackButton />
        </div>
        <div className="bg-white border rounded-lg p-16 text-center">
          <p className="text-xl font-semibold text-zinc-800 mb-2">Loading...</p>
        </div>
      </div>
    );
  }

  if (finalError || !record) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4 mb-4">
          <BackButton />
        </div>
        <div className="bg-white border rounded-lg p-16 text-center">
          <p className="text-xl font-semibold text-zinc-800 mb-2">
            No details exist for this record
          </p>
          <p className="text-zinc-500">
            The attendance record you're looking for doesn't exist or has been
            removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <BackButton />
        <h2 className="text-2xl font-bold text-zinc-800">
          {new Date(record.date + "T00:00:00").toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </h2>
      </div>

      <div className="bg-white border rounded-lg p-6 space-y-6">
        {/* Clock In Section */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-lg font-semibold text-zinc-800">Clock In</h3>
            {record.lateReason && (
              <button
                onClick={() => {
                  setSelectedLateReason(record.lateReason);
                  setShowLateReasonDialog(true);
                }}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full hover:bg-orange-100 transition-colors"
              >
                <Clock size={12} />
                Late
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              {renderLocationCard(
                "Clock in location",
                <MapPin className="w-5 h-5 mr-2" />,
                record.location &&
                  record.location !== "Gyalpozhing, Mongar Bhutan" &&
                  record.location !== "-"
                  ? record.location
                  : "No location",
                record.locationCoords,
              )}
            </div>
            <div>
              <div className="flex items-center mb-3">
                <Camera className="w-5 h-5 mr-2" />
                <span className="text-zinc-800">Clock in photo</span>
              </div>
              <div className="rounded-lg overflow-hidden border h-64 flex items-center justify-center bg-zinc-100 relative">
                {cleared ? (
                  <span className="text-zinc-500 text-center">
                    Image has been cleared by the system
                  </span>
                ) : imageLoadingStates.clockIn ? (
                  <span className="text-zinc-500 text-center">
                    Loading image...
                  </span>
                ) : clockInImageUrl ? (
                  <>
                    <img
                      src={clockInImageUrl}
                      alt="Clock in"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        const errorMsg = e.target.nextElementSibling;
                        if (errorMsg) {
                          errorMsg.style.display = "flex";
                        }
                      }}
                    />
                    <div className="hidden absolute inset-0 items-center justify-center">
                      <span className="text-zinc-500 text-center">
                        No image available
                      </span>
                    </div>
                  </>
                ) : (
                  <span className="text-zinc-500 text-center">
                    No image available
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Clock Out Section */}
        {record.clockOut && (
          <div>
            <h3 className="text-lg font-semibold text-zinc-800 mb-4">
              Clock Out
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                {renderLocationCard(
                  "Clock out location",
                  <MapPin className="w-5 h-5 mr-2" />,
                  record.clockOutLocation &&
                    record.clockOutLocation !== "Gyalpozhing, Mongar Bhutan" &&
                    record.clockOutLocation !== "-"
                    ? record.clockOutLocation
                    : "No location",
                  record.clockOutCoords,
                )}
              </div>
              <div>
                <div className="flex items-center mb-3">
                  <Camera className="w-5 h-5 mr-2" />
                  <span className="text-zinc-800">Clock out photo</span>
                </div>
                <div className="rounded-lg overflow-hidden border h-64 flex items-center justify-center bg-zinc-100 relative">
                  {cleared ? (
                    <span className="text-zinc-500 text-center">
                      Image has been cleared by the system
                    </span>
                  ) : imageLoadingStates.clockOut ? (
                    <span className="text-zinc-500 text-center">
                      Loading image...
                    </span>
                  ) : clockOutImageUrl ? (
                    <>
                      <img
                        src={clockOutImageUrl}
                        alt="Clock out"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const errorMsg = e.target.nextElementSibling;
                          if (errorMsg) {
                            errorMsg.style.display = "flex";
                          }
                        }}
                      />
                      <div className="hidden absolute inset-0 items-center justify-center">
                        <span className="text-zinc-500 text-center">
                          No image available
                        </span>
                      </div>
                    </>
                  ) : (
                    <span className="text-zinc-500 text-center">
                      No image available
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <LateReasonDialog
        show={showLateReasonDialog}
        onClose={() => {
          setShowLateReasonDialog(false);
          setSelectedLateReason("");
        }}
        lateReason={selectedLateReason}
      />
    </div>
  );
}
